import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import StageAverageChart from './StageAverageChart';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Componente para renderizar cada seção de filtro
const FilterSection = ({ title, icon, children }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
    <h5 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
      {icon} {title}
    </h5>
    {children}
  </div>
);

const LeadTimeBreakdownChart = ({ data, minDate, maxDate, plausible }) => {
  // Ref para capturar a área do gráfico para exportação
  const chartAreaRef = useRef(null);
  
  // Estado para controlar quais itens estão selecionados
  const [selectedItems, setSelectedItems] = useState(new Set());
  // Estado para manter histórico de itens selecionados por tipo
  const [selectedItemsByType, setSelectedItemsByType] = useState(new Map());
  const selectedItemsByTypeRef = useRef(new Map());
  // Estado para manter histórico de itens selecionados por período
  const [selectedItemsByPeriod, setSelectedItemsByPeriod] = useState(new Map());
  const selectedItemsByPeriodRef = useRef(new Map());
  // Estado para manter histórico de itens selecionados por busca
  const [selectedItemsBySearch, setSelectedItemsBySearch] = useState(new Map());
  const selectedItemsBySearchRef = useRef(new Map());
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  
  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Fixo em 20 itens por página
  
  // Estados para filtro de período com inicialização inteligente
  const [dateFilterEnabled, setDateFilterEnabled] = useState(false); // Inicializar desabilitado
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activePreset, setActivePreset] = useState(null); // Removido padrão fixo
  
  // Estados para controle de visualização
  const maxItemsToShow = 50; // Fixo em 50 itens
  const [sortBy, setSortBy] = useState('date'); // 'date', 'leadTime'
  
  // Estados para controle dos ícones de ajuda e personalização
  const [showTooltip, setShowTooltip] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  
  // Estados para controle da exportação
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Estados para personalização de cores
  const [stageColors, setStageColors] = useState({});
  
  // Cores padrão para as etapas
  const defaultColors = [
    '#3B82F6', // Azul
    '#10B981', // Verde
    '#F59E0B', // Amarelo
    '#EF4444', // Vermelho
    '#8B5CF6', // Roxo
    '#06B6D4', // Ciano
    '#F97316', // Laranja
    '#84CC16', // Lima
  ];

  // Obter colunas dinâmicas dos dados
  const dynamicColumns = useMemo(() => {
    if (!data || data.length === 0) return [];
    const firstItem = data[0];
    return firstItem.workflowColumns || [];
  }, [data]);
  
  // Função para obter o texto do preset
  const getPresetLabel = (preset) => {
    switch (preset) {
      case 'lastWeek':
        return 'Última Semana';
      case 'lastMonth':
        return 'Último Mês';
      case 'lastQuarter':
        return 'Último Trimestre';
      case 'lastSemester':
        return 'Último Semestre';
      case 'lastYear':
        return 'Último Ano';
      case 'currentMonth':
        return 'Mês Atual';
      case 'currentQuarter':
        return 'Trimestre Atual';
      default:
        return 'Período Personalizado';
    }
  };

  // Ordenar todos os itens por data de conclusão (mais recentes primeiro)
  const sortedAllItems = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].sort((a, b) => {
      const dateA = new Date(a['Closed Date']);
      const dateB = new Date(b['Closed Date']);
      return dateB - dateA; // Ordem decrescente (mais recentes primeiro)
    });
  }, [data]);

  // Obter todos os tipos únicos dos dados
  const allTypes = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const types = data.map(item => item['Tipo de Item']?.trim() || 'Item');
    return [...new Set(types)].sort();
  }, [data]);

  // Funções para filtros de data
  const getDateRangePreset = (preset) => {
    const today = new Date();
    const end = new Date(today);
    let start = new Date(today);
    
    switch (preset) {
      case 'lastWeek':
        start.setDate(today.getDate() - 7);
        break;
      case 'lastMonth':
        start.setMonth(today.getMonth() - 1);
        break;
      case 'lastQuarter':
        start.setMonth(today.getMonth() - 3);
        break;
      case 'lastSemester':
        start.setMonth(today.getMonth() - 6);
        break;
      case 'lastYear':
        start.setFullYear(today.getFullYear() - 1);
        break;
      case 'currentMonth':
        start.setDate(1);
        break;
      case 'currentQuarter':
        start.setMonth(Math.floor(today.getMonth() / 3) * 3, 1);
        break;
      default:
        return { start: '', end: '' };
    }
    
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10)
    };
  };

  // Inicializar tipos selecionados com todos os tipos disponíveis
  React.useEffect(() => {
    if (allTypes.length > 0 && selectedTypes.size === 0) {
      setSelectedTypes(new Set(allTypes));
    }
  }, [allTypes]);

  // Inicializar seleção com os últimos 50 itens por padrão
  React.useEffect(() => {
    if (sortedAllItems.length > 0 && selectedItems.size === 0) {
      // Selecionar os últimos 50 itens (mais recentes) por padrão
      const last50Items = sortedAllItems.slice(0, 50);
      const last50Ids = new Set(last50Items.map(item => item.ID || item.id));
      setSelectedItems(last50Ids);
    }
  }, [sortedAllItems]);

  // Filtrar itens baseado no texto de busca, tipos selecionados e período
  const filteredBySearch = useMemo(() => {
    let filtered = sortedAllItems;
    
    // Filtrar por tipos selecionados
    if (selectedTypes.size > 0) {
      filtered = filtered.filter(item => {
        const itemType = item['Tipo de Item']?.trim() || 'Item';
        return selectedTypes.has(itemType);
      });
    }
    
    // Filtrar por período de datas
    if (dateFilterEnabled && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      // Ajustar end para incluir o dia inteiro
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(item => {
        const closedDate = new Date(item['Closed Date']);
        return closedDate >= start && closedDate <= end;
      });
    }
    
    // Filtrar por texto de busca
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const itemId = (item.ID || item.id || '').toString().toLowerCase();
        return itemId.includes(searchLower);
      });
    }
    
    return filtered;
  }, [sortedAllItems, searchText, selectedTypes, dateFilterEnabled, startDate, endDate]);

  // Resetar página quando filtros mudarem
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedTypes, dateFilterEnabled, startDate, endDate]);

  // Detectar mudanças manuais nas datas para limpar preset ativo
  React.useEffect(() => {
    if (activePreset && dateFilterEnabled && startDate && endDate) {
      const presetDates = getDateRangePreset(activePreset);
      if (startDate !== presetDates.start || endDate !== presetDates.end) {
        setActivePreset(null); // Limpar preset se datas foram alteradas manualmente
      }
    }
  }, [startDate, endDate, activePreset, dateFilterEnabled]);

  // Calcular dados da paginação - usar itens filtrados
  const paginationData = useMemo(() => {
    const dataToShow = filteredBySearch; // Usar itens filtrados por busca e tipo
    const totalItems = dataToShow.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = dataToShow.slice(startIndex, endIndex);
    
    return {
      totalItems,
      totalPages,
      currentItems,
      startIndex,
      endIndex: Math.min(endIndex, totalItems),
      hasPreviousPage: currentPage > 1,
      hasNextPage: currentPage < totalPages
    };
  }, [filteredBySearch, currentPage, itemsPerPage]);

  // Atualizar seleção quando tipos selecionados mudarem
  React.useEffect(() => {
    if (selectedTypes.size > 0 && sortedAllItems.length > 0) {
      // Filtrar itens pelos tipos selecionados
      const itemsOfSelectedTypes = sortedAllItems.filter(item => {
        const itemType = item['Tipo de Item']?.trim() || 'Item';
        return selectedTypes.has(itemType);
      });
      
      const idsOfSelectedTypes = new Set(itemsOfSelectedTypes.map(item => item.ID || item.id));
      
      setSelectedItems(prev => {
        // Atualizar histórico por tipo ANTES de modificar a seleção
        const newHistory = new Map(selectedItemsByTypeRef.current);
        
        // Para cada tipo, salvar quais itens estavam selecionados
        allTypes.forEach(type => {
          const itemsOfThisType = sortedAllItems.filter(item => {
            const itemType = item['Tipo de Item']?.trim() || 'Item';
            return itemType === type;
          });
          
          const idsOfThisType = itemsOfThisType.map(item => item.ID || item.id);
          const selectedOfThisType = idsOfThisType.filter(id => prev.has(id));
          
          if (selectedOfThisType.length > 0) {
            newHistory.set(type, new Set(selectedOfThisType));
          }
        });
        
        // Atualizar o ref e o estado
        selectedItemsByTypeRef.current = newHistory;
        setSelectedItemsByType(newHistory);
        
        // Se nenhum tipo estava selecionado antes, selecionar os últimos 50 dos tipos atuais
        if (prev.size === 0) {
          const last50Items = itemsOfSelectedTypes.slice(0, 50);
          return new Set(last50Items.map(item => item.ID || item.id));
        }
        
        // Construir nova seleção baseada no histórico
        const newSelection = new Set();
        
        // Para cada tipo selecionado, recuperar do histórico ou selecionar automaticamente
        selectedTypes.forEach(type => {
          const itemsOfThisType = sortedAllItems.filter(item => {
            const itemType = item['Tipo de Item']?.trim() || 'Item';
            return itemType === type;
          });
          
          const idsOfThisType = new Set(itemsOfThisType.map(item => item.ID || item.id));
          
          // Se há histórico para este tipo, usar o histórico
          if (newHistory.has(type)) {
            const historicalSelection = newHistory.get(type);
            historicalSelection.forEach(id => {
              if (idsOfThisType.has(id)) {
                newSelection.add(id);
              }
            });
          } else {
            // Se não há histórico, verificar se já havia itens deste tipo selecionados
            const currentlySelectedOfThisType = [...prev].filter(id => idsOfThisType.has(id));
            currentlySelectedOfThisType.forEach(id => newSelection.add(id));
          }
        });
        
        // Se nenhum item foi selecionado mas todos os tipos estão selecionados, usar padrão de 50
        if (newSelection.size === 0 && selectedTypes.size === allTypes.length) {
          const last50Items = sortedAllItems.slice(0, 50);
          last50Items.forEach(item => newSelection.add(item.ID || item.id));
        }
        
        return newSelection;
      });
    } else if (selectedTypes.size === 0) {
      // Se nenhum tipo selecionado, limpar seleção
      setSelectedItems(new Set());
    }
  }, [selectedTypes, sortedAllItems, allTypes]);

  // Atualizar seleção quando filtro de período mudar
  React.useEffect(() => {
    // Criar chave para o período atual
    const periodKey = dateFilterEnabled && startDate && endDate 
      ? `${startDate}-${endDate}` 
      : 'no-period';
    
    // Se o período está sendo ativado
    if (dateFilterEnabled && startDate && endDate) {
      // Filtrar apenas pelos itens que estão no período
      const itemsInPeriod = sortedAllItems.filter(item => {
        const closedDate = new Date(item['Closed Date']);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return closedDate >= start && closedDate <= end;
      });
      
      const idsInPeriod = new Set(itemsInPeriod.map(item => item.ID || item.id));
      
      setSelectedItems(prev => {
        // PRIMEIRO salvar o estado anterior no histórico (se não for um período)
        const prevKey = 'no-period';
        if (!selectedItemsByPeriodRef.current.has(prevKey)) {
          const newHistory = new Map(selectedItemsByPeriodRef.current);
          newHistory.set(prevKey, new Set(prev));
          selectedItemsByPeriodRef.current = newHistory;
          setSelectedItemsByPeriod(newHistory);
        }
        
        // Se há histórico para este período específico, usar o histórico
        if (selectedItemsByPeriodRef.current.has(periodKey)) {
          const historicalSelection = selectedItemsByPeriodRef.current.get(periodKey);
          const validHistoricalItems = new Set([...historicalSelection].filter(id => idsInPeriod.has(id)));
          return validHistoricalItems;
        }
        
        // Se não há histórico, manter apenas os itens que estão no período (pode ser vazio)
        return new Set([...prev].filter(id => idsInPeriod.has(id)));
      });
    } else if (!dateFilterEnabled) {
      // Se o período foi desativado, tentar recuperar seleção anterior
      const noFilterKey = 'no-period';
      if (selectedItemsByPeriodRef.current.has(noFilterKey)) {
        const historicalSelection = selectedItemsByPeriodRef.current.get(noFilterKey);
        setSelectedItems(historicalSelection);
      }
    }
  }, [dateFilterEnabled, startDate, endDate, sortedAllItems]);

  // Atualizar seleção quando filtro de busca mudar
  React.useEffect(() => {
    // Criar chave para o estado de busca atual
    const searchKey = searchText.trim() ? `search-${searchText.trim().toLowerCase()}` : 'no-search';
    
    // Se há texto de busca sendo aplicado
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      const filteredIds = new Set(
        sortedAllItems
          .filter(item => {
            const itemId = (item.ID || item.id || '').toString().toLowerCase();
            return itemId.includes(searchLower);
          })
          .map(item => item.ID || item.id)
      );
      
      setSelectedItems(prev => {
        // PRIMEIRO salvar o estado anterior no histórico (se não há busca)
        const prevKey = 'no-search';
        if (!selectedItemsBySearchRef.current.has(prevKey)) {
          const newHistory = new Map(selectedItemsBySearchRef.current);
          newHistory.set(prevKey, new Set(prev));
          selectedItemsBySearchRef.current = newHistory;
          setSelectedItemsBySearch(newHistory);
        }
        
        // Se há histórico para esta busca específica, usar o histórico
        if (selectedItemsBySearchRef.current.has(searchKey)) {
          const historicalSelection = selectedItemsBySearchRef.current.get(searchKey);
          const validHistoricalItems = new Set([...historicalSelection].filter(id => filteredIds.has(id)));
          return validHistoricalItems;
        }
        
        // Se não há histórico, manter apenas os itens que correspondem à busca
        return new Set([...prev].filter(id => filteredIds.has(id)));
      });
    } else if (!searchText.trim()) {
      // Se o filtro de busca foi removido, tentar recuperar seleção anterior
      const noSearchKey = 'no-search';
      if (selectedItemsBySearchRef.current.has(noSearchKey)) {
        const historicalSelection = selectedItemsBySearchRef.current.get(noSearchKey);
        setSelectedItems(historicalSelection);
      }
    }
  }, [searchText, sortedAllItems]);

  // Filtrar dados baseado na seleção do usuário
  const filteredData = useMemo(() => {
    const selectedData = sortedAllItems.filter(item => {
      const itemId = item.ID || item.id;
      return selectedItems.has(itemId);
    });
    
    // Aplicar limitação de itens e ordenação para o gráfico
    let processedData = [...selectedData];
    
    if (sortBy === 'leadTime') {
      // Calcular lead time para ordenação
      processedData = processedData.map(item => {
        const firstItem = item;
        const dynamicColumns = firstItem.workflowColumns || [];
        let totalLeadTime = 0;
        
        dynamicColumns.forEach((column, index) => {
          const currentDate = item.workflowDates[column];
          if (currentDate) {
            let nextDate;
            if (index < dynamicColumns.length - 1) {
              const nextColumn = dynamicColumns[index + 1];
              nextDate = item.workflowDates[nextColumn];
            } else {
              nextDate = item['Closed Date'];
            }
            
            if (nextDate) {
              const duration = Math.ceil((nextDate - currentDate) / (1000 * 60 * 60 * 24));
              totalLeadTime += Math.max(0, duration);
            }
          }
        });
        
        return { ...item, calculatedLeadTime: totalLeadTime };
      }).sort((a, b) => b.calculatedLeadTime - a.calculatedLeadTime);
    } else {
      // Manter ordenação por data (já está ordenado por data de conclusão)
      processedData = selectedData;
    }
    
    // Limitar quantidade de itens
    return processedData.slice(0, maxItemsToShow);
  }, [sortedAllItems, selectedItems, maxItemsToShow, sortBy]);

  // Função para alternar seleção de um item
  const toggleItemSelection = (itemId) => {
    // Encontrar o tipo do item
    const item = sortedAllItems.find(item => (item.ID || item.id) === itemId);
    const itemType = item ? (item['Tipo de Item']?.trim() || 'Item') : null;
    
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      const isBeingSelected = !newSet.has(itemId);
      
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      
      // Atualizar histórico se o tipo é conhecido
      if (itemType && isBeingSelected) {
        const newHistory = new Map(selectedItemsByTypeRef.current);
        const currentHistoryForType = newHistory.get(itemType) || new Set();
        currentHistoryForType.add(itemId);
        newHistory.set(itemType, currentHistoryForType);
        
        selectedItemsByTypeRef.current = newHistory;
        setSelectedItemsByType(newHistory);
      }
      
      return newSet;
    });
  };

  // Funções para selecionar/deselecionar todos
  const selectAll = () => {
    const allIds = new Set(sortedAllItems.map(item => item.ID || item.id));
    setSelectedItems(allIds);
  };

  // Função para selecionar todos os itens filtrados pela busca
  const selectAllFiltered = () => {
    const filteredIds = new Set(filteredBySearch.map(item => item.ID || item.id));
    setSelectedItems(prev => new Set([...prev, ...filteredIds]));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  // Função para deselecionar todos os itens filtrados pela busca
  const deselectAllFiltered = () => {
    const filteredIds = new Set(filteredBySearch.map(item => item.ID || item.id));
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      filteredIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  };

  // Funções para gerenciar tipos
  const toggleType = (type) => {
    setSelectedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const selectAllTypes = () => {
    setSelectedTypes(new Set(allTypes));
  };

  const deselectAllTypes = () => {
    setSelectedTypes(new Set());
  };

  // Funções de navegação da paginação
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, paginationData.totalPages)));
  };

  const goToPreviousPage = () => {
    if (paginationData.hasPreviousPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (paginationData.hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const applyDatePreset = (preset) => {
    // Salvar seleção atual no histórico antes de aplicar o preset
    const currentKey = dateFilterEnabled && startDate && endDate 
      ? `${startDate}-${endDate}` 
      : 'no-period';
    
    const newHistory = new Map(selectedItemsByPeriodRef.current);
    newHistory.set(currentKey, new Set(selectedItems));
    selectedItemsByPeriodRef.current = newHistory;
    setSelectedItemsByPeriod(newHistory);
    
    const { start, end } = getDateRangePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setDateFilterEnabled(true);
    setActivePreset(preset); // Definir qual preset está ativo
  };

  const clearDateFilter = () => {
    // Salvar seleção atual no histórico antes de limpar o filtro
    if (dateFilterEnabled && startDate && endDate) {
      const currentKey = `${startDate}-${endDate}`;
      const newHistory = new Map(selectedItemsByPeriodRef.current);
      newHistory.set(currentKey, new Set(selectedItems));
      selectedItemsByPeriodRef.current = newHistory;
      setSelectedItemsByPeriod(newHistory);
    }
    
    setDateFilterEnabled(false);
    setStartDate('');
    setEndDate('');
    setActivePreset(null); // Limpar preset ativo
  };

  // Funções de exportação
  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    setShowExportModal(false);
    
    // Track the export action
    if (plausible) {
      plausible.trackExport('png', 'leadtime', 'leadtime-breakdown');
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'lead-time-breakdown-chart.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  const cancelExport = () => {
    setShowExportModal(false);
  };

  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return null;

    // Usar cores personalizadas se disponíveis, senão usar cores padrão
    const colors = dynamicColumns.map((column, index) => 
      stageColors[column] || defaultColors[index % defaultColors.length]
    );

    // Processar dados para cada item (manter ordem por data de conclusão)
    const processedData = filteredData.map(item => {
      const itemId = item.ID || item.id;
      const closedDate = item['Closed Date'];
      
      const stages = [];
      
      // Calcular duração de cada etapa dinâmica usando workflowDates
      dynamicColumns.forEach((column, index) => {
        const currentDate = item.workflowDates[column];
        if (currentDate) {
          let nextDate;
          
          // Determinar a próxima data (próxima coluna ou Closed Date)
          if (index < dynamicColumns.length - 1) {
            // Próxima coluna de workflow
            const nextColumn = dynamicColumns[index + 1];
            nextDate = item.workflowDates[nextColumn];
          } else {
            // Última coluna, usar Closed Date
            nextDate = closedDate;
          }
          
          if (nextDate) {
            const duration = Math.ceil((nextDate - currentDate) / (1000 * 60 * 60 * 24));
            stages.push({
              name: column,
              duration: Math.max(0, duration),
              color: colors[index % colors.length]
            });
          }
        }
      });
      
      return {
        itemId,
        stages,
        totalLeadTime: stages.reduce((sum, stage) => sum + stage.duration, 0)
      };
    });

    // Obter todas as etapas únicas (apenas as colunas dinâmicas)
    const allStages = [...dynamicColumns];
    
    // Preparar datasets para o gráfico empilhado
    const datasets = allStages.map((stageName, index) => ({
      label: stageName,
      data: processedData.map(item => {
        const stage = item.stages.find(s => s.name === stageName);
        return stage ? stage.duration : 0;
      }),
      backgroundColor: colors[index % colors.length],
      borderColor: colors[index % colors.length],
      borderWidth: 1,
    }));

    return {
      labels: processedData.map(item => item.itemId),
      datasets
    };
  }, [filteredData, stageColors, dynamicColumns, defaultColors]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          footer: function(tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
            
            // Obter a data de conclusão do item
            const itemIndex = tooltipItems[0]?.dataIndex;
            if (itemIndex !== undefined && filteredData && filteredData[itemIndex]) {
              const item = filteredData[itemIndex];
              const closedDate = new Date(item['Closed Date']).toLocaleDateString('pt-BR');
              return [`Total: ${total} dias`, `Data de conclusão: ${closedDate}`];
            }
            
            return `Total: ${total} dias`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Itens (ID)',
          font: {
            size: 13,
            weight: 600
          },
          color: '#374151'
        },
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          color: '#374151',
          font: {
            size: 13,
            weight: 500
          },
          maxRotation: 90,
          minRotation: 90
        }
      },
      y: {
        title: {
          display: true,
          text: 'Lead Time (dias)',
          font: {
            size: 13,
            weight: 600
          },
          color: '#374151'
        },
        stacked: true,
        beginAtZero: true,
        grid: {
          display: false
        },
        ticks: {
          color: '#374151',
          font: {
            size: 13,
            weight: 500
          }
        }
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md">
        <div className="text-center py-8 text-gray-500">
          <p>Nenhum dado válido encontrado para exibir o gráfico de breakdown.</p>
          <p className="text-sm text-gray-400 mt-2">Verifique se o arquivo contém as colunas obrigatórias e dados válidos.</p>
        </div>
      </div>
    );
  }

  if (!chartData || (selectedItems.size === 0 && paginationData.totalItems === 0)) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6 bg-white rounded-xl shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Lead Time Breakdown</h3>
        
        {/* Filtro de Seleção de Itens */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div 
            className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-lg transition-colors"
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
          >
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-gray-800">
                Filtro de Itens ({selectedItems.size} de {sortedAllItems.length} selecionados)
              </h4>
              {!isFilterExpanded && (
                <div className="flex items-center gap-2">
                  {searchText && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">🔍 ID</span>
                  )}
                  {selectedTypes.size > 0 && selectedTypes.size < allTypes.length && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">🏷️ Tipos</span>
                  )}
                </div>
              )}
            </div>
            <button 
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              type="button"
            >
              <span>{isFilterExpanded ? 'Colapsar' : 'Expandir'}</span>
              <span className={`transform transition-transform duration-300 ${isFilterExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
          </div>
      
          {/* Filtros Ativos */}
          {(searchText || (selectedTypes.size > 0 && selectedTypes.size < allTypes.length) || dateFilterEnabled) && (
            <div className="mt-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-blue-800">Filtros ativos:</span>
                
                {searchText && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs border border-blue-300">
                    🔍 ID: "{searchText}"
                    <button
                      onClick={() => setSearchText('')}
                      className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                      title="Remover filtro de busca"
                    >
                      ✕
                    </button>
                  </span>
                )}
                   {dateFilterEnabled && startDate && endDate && (
                <span 
                  className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs border border-indigo-300 cursor-help"
                  title={`Período: ${new Date(startDate).toLocaleDateString('pt-BR')} - ${new Date(endDate).toLocaleDateString('pt-BR')}`}
                >
                  📅 {activePreset ? getPresetLabel(activePreset) : `${new Date(startDate).toLocaleDateString('pt-BR')} - ${new Date(endDate).toLocaleDateString('pt-BR')}`}
                  <button
                    onClick={clearDateFilter}
                    className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                    title="Remover filtro de período"
                  >
                    ✕
                  </button>
                </span>
              )}
                
                {selectedTypes.size > 0 && selectedTypes.size < allTypes.length && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs border border-purple-300">
                    🏷️ {selectedTypes.size} de {allTypes.length} tipos
                    <button
                      onClick={selectAllTypes}
                      className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                      title="Selecionar todos os tipos"
                    >
                      ↻
                    </button>
                  </span>
                )}
                
                <button
                  onClick={() => {
                    setSearchText('');
                    clearDateFilter();
                    selectAllTypes();
                    setSelectedItems(new Set());
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs border border-red-300 hover:bg-red-200 transition-colors"
                  title="Limpar todos os filtros"
                >
                  🗑️ Limpar tudo
                </button>
              </div>
            </div>
          )}
          
          {/* Controles de Ordenação - sempre visível */}
          <div className="mt-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">📊 Ordenação:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="date">Data de conclusão (mais recentes)</option>
                <option value="leadTime">Lead time (maiores primeiro)</option>
              </select>
              {selectedItems.size > maxItemsToShow && (
                <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded border border-amber-300">
                  ⚠️ Mostrando {maxItemsToShow} de {selectedItems.size} selecionados
                </span>
              )}
            </div>
          </div>
      
          {isFilterExpanded && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coluna Esquerda - Filtros de Dados */}
              <div className="space-y-6">
                {/* Mensagem explicativa no topo */}
                <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                  <div className="text-blue-700 flex items-center gap-2">
                    💡 Por padrão, os últimos 50 itens mais recentes estão selecionados. Use os filtros abaixo para ajustar.
                  </div>
                </div>
                
                <FilterSection title="Buscar por ID" icon="🔍">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Digite o ID para filtrar..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchText && (
                        <button
                          onClick={() => setSearchText('')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {searchText && (
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        {filteredBySearch.length} de {sortedAllItems.length}
                      </span>
                    )}
                  </div>
                </FilterSection>

                <FilterSection title={`Tipos de Item (${selectedTypes.size}/${allTypes.length})`} icon="🏷️">
                  <div className="space-y-3">
                    <div className="flex gap-1">
                      <button
                        onClick={selectAllTypes}
                        className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors"
                      >
                        Todos
                      </button>
                      <button
                        onClick={deselectAllTypes}
                        className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs hover:bg-gray-100 transition-colors"
                      >
                        Nenhum
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allTypes.map(type => (
                        <label
                          key={type}
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full cursor-pointer transition-colors text-sm ${
                            selectedTypes.has(type)
                              ? 'bg-blue-100 text-blue-800 border border-blue-200'
                              : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTypes.has(type)}
                            onChange={() => toggleType(type)}
                            className="form-checkbox h-3 w-3 text-blue-600 rounded"
                          />
                          <span className="font-medium">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </FilterSection>

                <FilterSection title="Filtro por Período" icon="📅">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Períodos predefinidos:</span>
                      <button
                        onClick={clearDateFilter}
                        className={`px-2 py-1 rounded text-xs transition-colors ${
                          dateFilterEnabled 
                            ? 'bg-orange-50 text-orange-600 hover:bg-orange-100' 
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={!dateFilterEnabled}
                      >
                        Limpar
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => applyDatePreset('lastWeek')}
                        className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                          activePreset === 'lastWeek'
                            ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        Última Semana
                      </button>
                      <button
                        onClick={() => applyDatePreset('lastMonth')}
                        className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                          activePreset === 'lastMonth'
                            ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        Último Mês
                      </button>
                      <button
                        onClick={() => applyDatePreset('lastQuarter')}
                        className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                          activePreset === 'lastQuarter'
                            ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        Último Trimestre
                      </button>
                      <button
                        onClick={() => applyDatePreset('lastSemester')}
                        className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                          activePreset === 'lastSemester'
                            ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        Último Semestre
                      </button>
                    </div>
                    
                    <div>
                      <label className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={dateFilterEnabled}
                          onChange={(e) => setDateFilterEnabled(e.target.checked)}
                          className="form-checkbox h-4 w-4 text-indigo-600"
                        />
                        <span className="text-sm text-gray-700">Período personalizado:</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          disabled={!dateFilterEnabled}
                          className={`flex-1 px-2 py-1 border rounded text-sm ${
                            dateFilterEnabled 
                              ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' 
                              : 'border-gray-200 bg-gray-50 text-gray-400'
                          }`}
                        />
                        <span className="text-sm text-gray-500">até</span>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          disabled={!dateFilterEnabled}
                          className={`flex-1 px-2 py-1 border rounded text-sm ${
                            dateFilterEnabled 
                              ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' 
                              : 'border-gray-200 bg-gray-50 text-gray-400'
                          }`}
                        />
                      </div>
                    </div>
                    
                    {dateFilterEnabled && startDate && endDate && (
                      <div className="text-sm text-indigo-600 bg-indigo-50 p-2 rounded">
                        📅 Período ativo: {new Date(startDate).toLocaleDateString('pt-BR')} a {new Date(endDate).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>
                </FilterSection>
              </div>          {/* Coluna Direita - Seleção de Itens */}
          <div className="space-y-6">
            <FilterSection title="Lista de Itens" icon="📋">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Mostrando {paginationData.startIndex + 1} a {paginationData.endIndex} de {paginationData.totalItems} itens
                    {paginationData.totalPages > 1 && ` • Página ${currentPage} de ${paginationData.totalPages}`}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                    >
                      Todos
                    </button>
                    <button
                      onClick={deselectAll}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors"
                    >
                      Nenhum
                    </button>
                  </div>
                </div>
                    
                    <div className="border border-gray-200 rounded">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2 min-h-[200px] max-h-[400px] overflow-y-auto">
                        {paginationData.currentItems.map((item, index) => {
                          const itemId = item.ID || item.id;
                          const closedDate = new Date(item['Closed Date']).toLocaleDateString('pt-BR');
                          const isSelected = selectedItems.has(itemId);
                          
                          return (
                            <label 
                              key={itemId}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 transition-colors ${
                                isSelected ? 'bg-blue-50 border border-blue-200' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(itemId)}
                                className="form-checkbox h-4 w-4 text-blue-600"
                              />
                              <div className="text-sm flex-1">
                                <div className="font-medium text-gray-800">
                                  {itemId}
                                </div>
                                <div className="text-xs text-gray-500">{closedDate}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Sempre mostrar controles de paginação, mesmo com 1 página */}
                    {paginationData.totalItems > 0 && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={goToPreviousPage}
                          disabled={!paginationData.hasPreviousPage}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            paginationData.hasPreviousPage
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          ← Anterior
                        </button>
                        
                        {paginationData.totalPages > 1 ? (
                          <div className="flex gap-1">
                            {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                              let pageNumber;
                              if (paginationData.totalPages <= 5) {
                                pageNumber = i + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1;
                              } else if (currentPage >= paginationData.totalPages - 2) {
                                pageNumber = paginationData.totalPages - 4 + i;
                              } else {
                                pageNumber = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => goToPage(pageNumber)}
                                  className={`px-3 py-1 rounded text-sm transition-colors ${
                                    currentPage === pageNumber
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            Página 1 de 1
                          </div>
                        )}
                        
                        <button
                          onClick={goToNextPage}
                          disabled={!paginationData.hasNextPage}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            paginationData.hasNextPage
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Próxima →
                        </button>
                      </div>
                    )}
                  </div>
                </FilterSection>
              </div>
            </div>
          )}
        </div>

        <div className="text-center py-8 text-gray-500">
          <p>Selecione pelo menos um item para exibir o gráfico.</p>
          <p className="text-sm text-gray-400 mt-2">Use o filtro acima para escolher quais itens visualizar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6 bg-white rounded-xl shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-800">Lead Time Breakdown</h3>
          
          {/* Tooltip de Explicação ao lado do título */}
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="Como interpretar este gráfico"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            
            {/* Tooltip de Ajuda */}
            {showTooltip && (
              <div className="absolute left-0 top-full mt-2 w-96 bg-gray-800 text-white text-sm rounded-lg shadow-lg p-5 z-10">
                <div className="font-semibold mb-3 text-blue-200">📊 Como interpretar este gráfico:</div>
                <div className="space-y-2 text-xs">
                  <div className="border-l-2 border-blue-400 pl-3">
                    <div className="font-medium text-blue-200 mb-1">🔍 Estrutura do gráfico:</div>
                    <div>• Cada barra vertical = um item do seu fluxo (ex: US-001)</div>
                    <div>• Altura da barra = tempo total que o item levou para ser concluído desde o ponto de comprometimento</div>
                    <div>• Cores diferentes = etapas do seu processo de trabalho</div>
                  </div>
                  
                  <div className="border-l-2 border-green-400 pl-3">
                    <div className="font-medium text-green-200 mb-1">⏱️ Como o tempo é calculado:</div>
                    <div>• Cada etapa mostra quanto tempo o item ficou nela</div>
                    <div>• Exemplo: "Em Desenvolvimento" por 5 dias, "Em Testes" por 3 dias</div>
                    <div>• A soma de todas as etapas = Lead Time total</div>
                  </div>
                  
                  <div className="border-l-2 border-yellow-400 pl-3">
                    <div className="font-medium text-yellow-200 mb-1">💡 Dicas de uso:</div>
                    <div>• Passe o mouse sobre as barras para ver detalhes</div>
                    <div>• Use os filtros para analisar períodos específicos</div>
                    <div>• Compare barras altas com baixas para identificar padrões</div>
                  </div>
                </div>
                <div className="absolute top-0 left-6 transform -translate-y-1 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Ícone de Personalização */}
          <button
            onClick={() => setShowCustomization(true)}
            className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            title="Personalizar cores das etapas"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          
          {/* Botão de Exportar */}
          <button
            onClick={handleExportClick}
            className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium"
            title="Exportar gráfico como imagem"
          >
            <span className="mr-2">📸</span>
            Exportar
          </button>
        </div>
      </div>
      
      {/* Filtro de Seleção de Itens - versão com gráfico */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div 
          className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-lg transition-colors"
          onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        >
          <div className="flex items-center gap-3">
            <h4 className="font-semibold text-gray-800">
              Filtro de Itens ({selectedItems.size} de {sortedAllItems.length} selecionados)
            </h4>
            {!isFilterExpanded && (
              <div className="flex items-center gap-2">
                {searchText && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">🔍 ID</span>
                )}
                {selectedTypes.size > 0 && selectedTypes.size < allTypes.length && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">🏷️ Tipos</span>
                )}
              </div>
            )}
          </div>
          <button 
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            type="button"
          >
            <span>{isFilterExpanded ? 'Colapsar' : 'Expandir'}</span>
            <span className={`transform transition-transform duration-300 ${isFilterExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
        </div>
        
        {/* Filtros Ativos */}
        {(searchText || (selectedTypes.size > 0 && selectedTypes.size < allTypes.length) || dateFilterEnabled) && (
          <div className="mt-3 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-blue-800">Filtros ativos:</span>
              
              {searchText && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs border border-blue-300">
                  🔍 ID: "{searchText}"
                  <button
                    onClick={() => setSearchText('')}
                    className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                    title="Remover filtro de busca"
                  >
                    ✕
                  </button>
                </span>
              )}
                 {dateFilterEnabled && startDate && endDate && (
              <span 
                className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs border border-indigo-300 cursor-help"
                title={`Período: ${new Date(startDate).toLocaleDateString('pt-BR')} - ${new Date(endDate).toLocaleDateString('pt-BR')}`}
              >
                📅 {activePreset ? getPresetLabel(activePreset) : `${new Date(startDate).toLocaleDateString('pt-BR')} - ${new Date(endDate).toLocaleDateString('pt-BR')}`}
                <button
                  onClick={clearDateFilter}
                  className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                  title="Remover filtro de período"
                >
                  ✕
                </button>
              </span>
            )}
              
              {selectedTypes.size > 0 && selectedTypes.size < allTypes.length && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs border border-purple-300">
                  🏷️ {selectedTypes.size} de {allTypes.length} tipos
                  <button
                    onClick={selectAllTypes}
                    className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                    title="Selecionar todos os tipos"
                  >
                    ↻
                  </button>
                </span>
              )}
              
              <button
                onClick={() => {
                  setSearchText('');
                  clearDateFilter();
                  selectAllTypes();
                  setSelectedItems(new Set());
                }}
                className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs border border-red-300 hover:bg-red-200 transition-colors"
                title="Limpar todos os filtros"
              >
                🗑️ Limpar tudo
              </button>
            </div>
          </div>
        )}
        
        {/* Controles de Ordenação - sempre visível */}
        <div className="mt-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">📊 Ordenação:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="date">Data de conclusão (mais recentes)</option>
              <option value="leadTime">Lead time (maiores primeiro)</option>
            </select>
            {selectedItems.size > maxItemsToShow && (
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded border border-amber-300">
                ⚠️ Mostrando {maxItemsToShow} de {selectedItems.size} selecionados
              </span>
            )}
          </div>
        </div>
        
        {/* Mesma estrutura organizada para quando há gráfico */}
        {isFilterExpanded && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Esquerda - Filtros de Dados */}
            <div className="space-y-6">
              {/* Mensagem explicativa no topo */}
              <div className="text-sm bg-blue-50 p-3 rounded border border-blue-200">
                <div className="text-blue-700 flex items-center gap-2">
                  💡 Por padrão, os últimos 50 itens mais recentes estão selecionados. Use os filtros abaixo para ajustar.
                </div>
              </div>
              
              <FilterSection title="Buscar por ID" icon="🔍">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Digite o ID para filtrar..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchText && (
                      <button
                        onClick={() => setSearchText('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {searchText && (
                    <span className="text-sm text-gray-600 whitespace-nowrap">
                      {filteredBySearch.length} de {sortedAllItems.length}
                    </span>
                  )}
                </div>
              </FilterSection>

              <FilterSection title={`Tipos de Item (${selectedTypes.size}/${allTypes.length})`} icon="🏷️">
                <div className="space-y-3">
                  <div className="flex gap-1">
                    <button
                      onClick={selectAllTypes}
                      className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors"
                    >
                      Todos
                    </button>
                    <button
                      onClick={deselectAllTypes}
                      className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs hover:bg-gray-100 transition-colors"
                    >
                      Nenhum
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allTypes.map(type => (
                      <label
                        key={type}
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full cursor-pointer transition-colors text-sm ${
                          selectedTypes.has(type)
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedTypes.has(type)}
                          onChange={() => toggleType(type)}
                          className="form-checkbox h-3 w-3 text-blue-600 rounded"
                        />
                        <span className="font-medium">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </FilterSection>

              <FilterSection title="Filtro por Período" icon="📅">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Períodos predefinidos:</span>
                    <button
                      onClick={clearDateFilter}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        dateFilterEnabled 
                          ? 'bg-orange-50 text-orange-600 hover:bg-orange-100' 
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                      disabled={!dateFilterEnabled}
                    >
                      Limpar
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => applyDatePreset('lastWeek')}
                      className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                        activePreset === 'lastWeek'
                          ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      Última Semana
                    </button>
                    <button
                      onClick={() => applyDatePreset('lastMonth')}
                      className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                        activePreset === 'lastMonth'
                          ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      Último Mês
                    </button>
                    <button
                      onClick={() => applyDatePreset('lastQuarter')}
                      className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                        activePreset === 'lastQuarter'
                          ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      Último Trimestre
                    </button>
                    <button
                      onClick={() => applyDatePreset('lastSemester')}
                      className={`px-3 py-2 rounded text-sm transition-colors font-medium ${
                        activePreset === 'lastSemester'
                          ? 'bg-indigo-600 text-white border-2 border-indigo-700 shadow-md'
                          : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                      }`}
                    >
                      Último Semestre
                    </button>
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={dateFilterEnabled}
                        onChange={(e) => setDateFilterEnabled(e.target.checked)}
                        className="form-checkbox h-4 w-4 text-indigo-600"
                      />
                      <span className="text-sm text-gray-700">Período personalizado:</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={!dateFilterEnabled}
                        className={`flex-1 px-2 py-1 border rounded text-sm ${
                          dateFilterEnabled 
                            ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' 
                            : 'border-gray-200 bg-gray-50 text-gray-400'
                        }`}
                      />
                      <span className="text-sm text-gray-500">até</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!dateFilterEnabled}
                        className={`flex-1 px-2 py-1 border rounded text-sm ${
                          dateFilterEnabled 
                            ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500' 
                            : 'border-gray-200 bg-gray-50 text-gray-400'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {dateFilterEnabled && startDate && endDate && (
                    <div className="text-sm text-indigo-600 bg-indigo-50 p-2 rounded">
                      📅 Período ativo: {new Date(startDate).toLocaleDateString('pt-BR')} a {new Date(endDate).toLocaleDateString('pt-BR')}
                    </div>
                  )}
                </div>
              </FilterSection>
            </div>

            {/* Coluna Direita - Seleção de Itens */}
            <div className="space-y-6">
              <FilterSection title="Lista de Itens" icon="📋">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      {paginationData.totalItems > 0 ? (
                        <>
                          Mostrando {paginationData.startIndex + 1} a {paginationData.endIndex} de {paginationData.totalItems} itens
                          {paginationData.totalPages > 1 && ` • Página ${currentPage}/${paginationData.totalPages}`}
                        </>
                      ) : (
                        'Nenhum item encontrado'
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={selectAll}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                      >
                        Todos
                      </button>
                      <button
                        onClick={deselectAll}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors"
                      >
                        Nenhum
                      </button>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2 min-h-[200px] max-h-[400px] overflow-y-auto">
                      {paginationData.currentItems.map((item, index) => {
                        const itemId = item.ID || item.id;
                        const closedDate = new Date(item['Closed Date']).toLocaleDateString('pt-BR');
                        const isSelected = selectedItems.has(itemId);
                        
                        return (
                          <label 
                            key={itemId}
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 transition-colors ${
                              isSelected ? 'bg-blue-50 border border-blue-200' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleItemSelection(itemId)}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            <div className="text-sm flex-1">
                              <div className="font-medium text-gray-800">{itemId}</div>
                              <div className="text-xs text-gray-500">{closedDate}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    
                    {paginationData.totalItems === 0 && searchText && (
                      <div className="p-4 text-center text-gray-500">
                        <p>Nenhum item encontrado para "{searchText}"</p>
                      </div>
                    )}
                    
                    {paginationData.totalItems === 0 && !searchText && selectedTypes.size === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        <p>Selecione pelo menos um tipo de item para visualizar a lista</p>
                      </div>
                    )}
                    
                    {paginationData.totalItems === 0 && !searchText && selectedTypes.size > 0 && dateFilterEnabled && (
                      <div className="p-4 text-center text-gray-500">
                        <p>Nenhum item encontrado no período selecionado</p>
                        <button
                          onClick={() => setDateFilterEnabled(false)}
                          className="mt-2 px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
                        >
                          Mostrar todos os períodos
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {paginationData.totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={goToPreviousPage}
                        disabled={!paginationData.hasPreviousPage}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          paginationData.hasPreviousPage
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        ← Anterior
                      </button>
                      
                      <div className="flex gap-1">
                        {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                          let pageNumber;
                          if (paginationData.totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= paginationData.totalPages - 2) {
                            pageNumber = paginationData.totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => goToPage(pageNumber)}
                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                currentPage === pageNumber
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={goToNextPage}
                        disabled={!paginationData.hasNextPage}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          paginationData.hasNextPage
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Próxima →
                      </button>
                    </div>
                  )}
                </div>
              </FilterSection>
            </div>
          </div>
        )}
      </div>
      
      <div ref={chartAreaRef} className="h-96 mb-6" style={{ background: '#fff', borderRadius: 8, padding: 16 }}>
        <Bar data={chartData} options={options} />
      </div>

      {/* Modal de Confirmação de Exportação */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3">📸</span>
              <h3 className="text-lg font-semibold text-gray-900">Exportar Gráfico</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Deseja fazer o download do gráfico de Lead Time Breakdown como imagem PNG?
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelExport}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
              >
                Fazer Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Personalização */}
      {showCustomization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCustomization(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Personalizar Cores das Etapas</h3>
                <button
                  onClick={() => setShowCustomization(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {dynamicColumns.map((stage, index) => (
                  <div key={stage} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 flex-1 mr-3">{stage}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={stageColors[stage] || defaultColors[index % defaultColors.length]}
                        onChange={(e) => setStageColors(prev => ({ ...prev, [stage]: e.target.value }))}
                        className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
                      />
                      {stageColors[stage] && (
                        <button
                          onClick={() => setStageColors(prev => {
                            const updated = { ...prev };
                            delete updated[stage];
                            return updated;
                          })}
                          className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                          title="Restaurar cor padrão"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setStageColors({})}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                >
                  Restaurar Todas
                </button>
                <button
                  onClick={() => setShowCustomization(false)}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Pronto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gráfico de Tempo Médio por Etapa */}
      <StageAverageChart 
        data={filteredData} 
        stageColors={stageColors}
        defaultColors={defaultColors}
        dynamicColumns={dynamicColumns}
      />
    </div>
  );
};

export default LeadTimeBreakdownChart;
